import { useCallback, useEffect, useRef, useState } from 'react';
import { StepWrapper } from '../StepWrapper';
import {
  CompanyDetailsSection,
  type BankAccountSelectionState,
  type CompanyImportData,
  type SectionHandle,
} from '@/pages/settings-general';
import { useWizardMode } from '@/contexts/WizardModeContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import { apiFetch } from '@/lib/api-error-handler';
import { getApiBaseUrl } from '@/lib/data-service';

// Demo fallback — used only in demo mode when no real finance integration exists
const DEMO_COMPANY_DATA: CompanyImportData = {
  name: 'Meridian Consulting Group Ltd',
  companyRegistrationNumber: '08412657',
  vatNumber: 'GB 214 8867 31',
  phone: '+44 20 7946 0812',
  email: 'finance@meridiancg.co.uk',
  website: 'www.meridiancg.co.uk',
  registeredAddress: {
    line1: '4th Floor, One Canada Square',
    line2: 'Canary Wharf',
    city: 'London',
    county: 'Greater London',
    postcode: 'E14 5AB',
    country: 'United Kingdom',
  },
  bankDetails: {
    bankName: 'Barclays Bank PLC',
    accountName: 'Meridian Consulting Group Ltd',
    sortCode: '20-32-06',
    accountNumber: '63847291',
    iban: 'GB29 BARC 2032 0663 8472 91',
    swiftBic: 'BARCGB22',
  },
  bankAccounts: [
    {
      accountKey: 1,
      accountCode: 'BANK01',
      accountName: 'Current Account',
      label: 'BANK01 — Current Account — 63847291',
      bankName: 'Barclays Bank PLC',
      bankAccountName: 'Meridian Consulting Group Ltd',
      sortCode: '20-32-06',
      accountNumber: '63847291',
      iban: 'GB29 BARC 2032 0663 8472 91',
      swiftBic: 'BARCGB22',
    },
    {
      accountKey: 2,
      accountCode: 'BANK02',
      accountName: 'Client Account',
      label: 'BANK02 — Client Account — 63847315',
      bankName: 'Barclays Bank PLC',
      bankAccountName: 'Meridian Consulting Group Client Account',
      sortCode: '20-32-06',
      accountNumber: '63847315',
      iban: 'GB48 BARC 2032 0663 8473 15',
      swiftBic: 'BARCGB22',
    },
  ],
  selectedBankAccountKey: 1,
  selectedBankAccountCode: 'BANK01',
};

interface StepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  isLastStep: boolean;
}

export function Step1CompanySetup({ onNext, onBack, onSkip, isLastStep }: StepProps) {
  const { registerSave } = useWizardMode();
  const { isDemoMode } = useDataMode();
  const { financeStatus } = useDashboardIntegrationStatus();
  const sectionRef = useRef<SectionHandle>(null);
  const [importData, setImportData] = useState<CompanyImportData | undefined>(undefined);
  const [bankSelection, setBankSelection] = useState<BankAccountSelectionState>({ accountCount: 0, hasSelection: false });

  const handleBankSelectionChange = useCallback((state: BankAccountSelectionState) => {
    setBankSelection((prev) =>
      prev.accountCount === state.accountCount && prev.hasSelection === state.hasSelection ? prev : state);
  }, []);

  const bankAccountRequired = bankSelection.accountCount > 0 && !bankSelection.hasSelection;

  // Fetch company details from finance integration API
  useEffect(() => {
    if (isDemoMode) {
      setImportData(DEMO_COMPANY_DATA);
      return;
    }
    if (!financeStatus.isConnected) return;

    apiFetch<CompanyImportData>(`${getApiBaseUrl()}/integrations/finance/company-details`)
      .then((data) => {
        if (data) setImportData(data);
      })
      .catch(() => {
        // Finance integration connected but endpoint unavailable — leave importData undefined
      });
  }, [isDemoMode, financeStatus.isConnected]);

  // Register the section's save so the wizard persists company details on Next.
  useEffect(() => {
    registerSave(async () => { await sectionRef.current?.save(); });
  }, [registerSave]);

  return (
    <StepWrapper
      barState={bankAccountRequired ? 'blocked' : 'default'}
      barMessage={
        bankAccountRequired
          ? 'Select the bank account to use before continuing.'
          : 'These details appear on all invoices and reports generated in Projects.'
      }
      showBack={true}
      showSkip={true}
      nextDisabled={bankAccountRequired}
      nextLabel={isLastStep ? 'Finish Setup →' : 'Next Step →'}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
    >
      <div className="rounded-lg border border-border overflow-hidden bg-background shadow-sm">
        <CompanyDetailsSection
          ref={sectionRef}
          importData={importData}
          readOnlyDetails
          onBankAccountSelectionChange={handleBankSelectionChange}
        />
      </div>
    </StepWrapper>
  );
}
